import { URLS } from '@/data/config';
import { createActiveWalletJurisdictionHeaders, useActiveWalletStore } from '@/hooks/use-active-wallet';
import { logger } from '@/utils/logging';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

export type TopUpType = 'daily_top_up' | 'crossover_top_up';

const TOP_UP_DETAILS_KEY = ['top-up-details'];

type TopUpDetails = {
    data: {
        coin_top_up_amount: number;
        cash_top_up_amount: number;
        next_top_up_at: string;
    };
    servertime: string;
};

/**
 * Get top up details
 */
const getTopUpDetails = async (): Promise<TopUpDetails> => {
    const response = await fetch(`${URLS.CHAMELON_API_URL}/auth/sweeps/top-up-details`, {
        method: 'GET',
        headers: {
            ...createActiveWalletJurisdictionHeaders(),
            'Content-Type': 'application/json',
        },
    });

    const data = await response.json();
    if (!response.ok) {
        logger.debug('[Top up]', 'Error fetching top up details:', data);
        throw new Error(data.message || 'Error fetching top up details');
    }
    return data;
};

const postClaimTopUp = async (topUpType: TopUpType): Promise<TopUpDetails> => {
    const response = await fetch(`${URLS.CHAMELON_API_URL}/auth/sweeps/claim-top-up`, {
        method: 'POST',
        headers: {
            ...createActiveWalletJurisdictionHeaders(),
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({ top_up_type: topUpType }),
    });

    const data = await response.json();
    if (!response.ok) {
        logger.debug('[Top up]', 'Error claiming top up:', data);
        throw new Error(data.message || 'Error claiming top up');
    }
    return data;
};

export const useTopUp = () => {
    const query = useQuery({
        queryKey: TOP_UP_DETAILS_KEY,
        queryFn: getTopUpDetails,
        select: data => {
            return {
                ...data.data,
                current_timestamp: data.servertime,
            };
        },
    });

    return query;
};

export const useClaimTopUp = () => {
    const loadWallets = useActiveWalletStore(state => state.loadWallets);
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: postClaimTopUp,
        onSuccess: () => {
            loadWallets();
            // The POST returns the amounts just credited, not remaining eligibility - refetch for that.
            queryClient.invalidateQueries({ queryKey: TOP_UP_DETAILS_KEY });
        },
    });
};
